import React from "react";
import FieldCard from "./FieldCard";
import InputField from "./InputField";

function PaintingSectionCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
      <div className="mb-3 text-sm font-semibold text-white">{title}</div>
      {children}
    </div>
  );
}

function createDefaultSection(base = {}) {
  return {
    id: crypto.randomUUID(),
    name: "",
    areaM2: "",
    surfaceType: "wall",
    prepLevel: "standard",
    coats: 2,
    ...base,
  };
}

function normalizeSectionForEditor(section = {}) {
  return createDefaultSection({
    ...section,
    id: section.id || crypto.randomUUID(),
    surfaceType: section.surfaceType || "wall",
    prepLevel: section.prepLevel || "standard",
    coats:
      section.coats === "" ? "" : Number(section.coats ?? 2) || 2,
  });
}

export default function PaintingEditor({
  parsedData,
  setParsedData,
  errors = {},
}) {
  const sections =
    Array.isArray(parsedData?.paintingSections) &&
    parsedData.paintingSections.length
      ? parsedData.paintingSections.map(normalizeSectionForEditor)
      : [
          normalizeSectionForEditor({
            name: "",
            areaM2: parsedData.paintingAreaM2 ?? "",
            surfaceType: parsedData.surfaceType || "wall",
            prepLevel: parsedData.prepLevel || "standard",
            coats: parsedData.coats ?? 2,
          }),
        ];

  const setSections = (nextSections) => {
    const normalizedSections = (Array.isArray(nextSections) ? nextSections : []).map(
      normalizeSectionForEditor
    );

    setParsedData((prev) => ({
      ...prev,
      projectType: "painting",
      primaryProjectType: "painting",
      projectTypes: ["painting"],
      paintingSections: normalizedSections,
      paintingAreaM2: normalizedSections[0]?.areaM2 ?? "",
      surfaceType: normalizedSections[0]?.surfaceType || "wall",
      prepLevel: normalizedSections[0]?.prepLevel || "standard",
      coats: normalizedSections[0]?.coats ?? 2,
    }));
  };

  const addSection = () => {
    setSections([...sections, createDefaultSection()]);
  };

  const updateSection = (index, field, value) => {
    setSections(
      sections.map((section, i) =>
        i === index
          ? normalizeSectionForEditor({ ...section, [field]: value })
          : section
      )
    );
  };

  const removeSection = (index) => {
    const nextSections = sections.filter((_, i) => i !== index);

    setSections(
      nextSections.length ? nextSections : [createDefaultSection()]
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-400">
        Define one or more painting sections. Enter area, surface type, prep
        level, and number of coats for each section separately.
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={addSection}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add painting section
        </button>
      </div>

      <div className="space-y-4">
        {sections.map((section, index) => (
          <PaintingSectionCard
            key={section.id || `painting-${index}`}
            title={section.name?.trim() || `Painting section ${index + 1}`}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FieldCard
                label="Section name"
                warning="Optional internal name like Kitchen, North facade, Stairwell."
              >
                <InputField
                  value={section.name ?? ""}
                  onChange={(e) => updateSection(index, "name", e.target.value)}
                  placeholder="e.g. Kitchen"
                />
              </FieldCard>

              <FieldCard
                label="Painting area"
                unit="m²"
                error={
                  errors[`paintingSections.${index}`] ||
                  (index === 0 ? errors.paintingAreaM2 : "")
                }
                warning="Total surface to be painted for this section."
              >
                <InputField
                  type="number"
                  value={section.areaM2 ?? ""}
                  onChange={(e) =>
                    updateSection(
                      index,
                      "areaM2",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 55"
                />
              </FieldCard>

              <FieldCard
                label="Surface type"
                warning="Choose the main surface to be painted."
              >
                <select
                  value={section.surfaceType || "wall"}
                  onChange={(e) =>
                    updateSection(index, "surfaceType", e.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="wall">Wall</option>
                  <option value="ceiling">Ceiling</option>
                  <option value="woodwork">Woodwork / trim</option>
                  <option value="facade">Facade / exterior</option>
                  <option value="other">Other</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Prep level"
                warning="How much prep is needed before painting?"
              >
                <select
                  value={section.prepLevel || "standard"}
                  onChange={(e) =>
                    updateSection(index, "prepLevel", e.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="light">Light prep</option>
                  <option value="standard">Standard prep</option>
                  <option value="heavy">Heavy prep</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Number of coats"
                warning="Use the expected number of paint coats."
              >
                <select
                  value={section.coats || 2}
                  onChange={(e) =>
                    updateSection(index, "coats", Number(e.target.value))
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>1 coat</option>
                  <option value={2}>2 coats</option>
                  <option value={3}>3 coats</option>
                </select>
              </FieldCard>

              {index === 0 && (
                <FieldCard
                  label="Finish level"
                  warning="Use premium if the finish expectations are higher than normal."
                >
                  <select
                    value={parsedData.finishLevel || "standard"}
                    onChange={(e) =>
                      setParsedData((prev) => ({
                        ...prev,
                        projectType: "painting",
                        primaryProjectType: "painting",
                        projectTypes: ["painting"],
                        finishLevel: e.target.value,
                        paintingSections: sections,
                      }))
                    }
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </FieldCard>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => removeSection(index)}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove section
              </button>
            </div>
          </PaintingSectionCard>
        ))}
      </div>
    </div>
  );
}